import { useNavigate } from "react-router-dom";
import {
  PaymentInput,
  useSubmitShopNowV2CheckoutMutation,
} from "../../../gql/__generated__/schema";
import { CONFIRMATION_ROUTE } from "../../components/AppRouter";
import { SubmitPaymentCheckoutFn } from "./usePayment";
import { useState } from "react";
import errorNotifier from "../../../shared/modules/error-notifier";
import { getTranslation } from "../../../shared/modules/config";

interface SubmitProps {
  paymentRequired: boolean;
  makePayment: SubmitPaymentCheckoutFn;
  paymentClientSecret: string;
  code: string;
  cartToken: string;
  updateConsumerPreferences: () => Promise<void>;
}

const useSubmit = ({
  paymentRequired,
  makePayment,
  code,
  cartToken,
  updateConsumerPreferences,
}: SubmitProps) => {
  const navigate = useNavigate();
  const [
    submitCheckoutAPI,
    { data: submitData, loading: loadingSubmitCheckout, error: apiError },
  ] = useSubmitShopNowV2CheckoutMutation();
  const [error, setError] = useState("");

  const submitCheckoutComplete = async (payment?: PaymentInput | null) => {
    const response = await submitCheckoutAPI({
      variables: {
        checkout: {
          code,
          cartToken,
          payment,
        },
      },
    });
    const { errors } = response.data?.shopNowV2Checkout ?? {};
    if (errors?.[0]) {
      errorNotifier.critical("There was an error in Shop Now V2 checkout API", {
        details: errors[0],
      });
      throw new Error();
    }
  };

  const onSubmitHandler = async () => {
    try {
      if (paymentRequired) {
        const result = await makePayment();
        await submitCheckoutComplete({
          gateway: "stripe",
          paymentIntentId: result.paymentId,
        });
      } else {
        await submitCheckoutComplete();
      }

      await updateConsumerPreferences();
      navigate(CONFIRMATION_ROUTE);
    } catch (error) {
      setError(getTranslation("shop_now_checkout_submission_error_default"));
      errorNotifier.critical("Shop Now V2 checkout submission error", {
        details: error,
      });
    }
  };

  return {
    onSubmitHandler,
    submitData,
    submitError: apiError?.message ?? error,
    loadingSubmitCheckout,
  };
};

export default useSubmit;
